<template>
  <div class="text-input" ref="wrapperEl">
    <div class="text-input__label" v-if="fields.outerLabel?.key" :id="labelId">
      <TipTap v-model="outerLabelValue" mode="single" />
    </div>
    <BaseInputShell v-bind="shellProps" :error="hasError">
      <BaseInputField
        :id="inputId"
        :name="resolvedAutocomplete"
        :modelValue="value"
        @update:modelValue="onInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @submit="handleSubmit"
        :placeholder="placeholderText"
        :autofocus="fields.autoFocus"
        :autocomplete="resolvedAutocomplete"
        :aria-labelledby="fields.outerLabel?.key ? labelId : undefined"
        :aria-describedby="describedBy(showValidationMessage && errorId)"
        :aria-invalid="hasError"
        v-bind="fieldProps"
      />
    </BaseInputShell>
    <div class="text-input__error" v-if="showValidationMessage" :id="errorId">
      <TipTap v-model="validationMessageValue" mode="single" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { usePipe } from '@render/composables/usePipe'
import { useFormInput } from '@render/composables/useFormInput'
import { useTranslation } from '@render/composables/useTranslation'
import { useInputAria } from '@render/composables/useInputAria'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import { getColorStyleData, getSizeStyleData } from '@render/composables/useStyleData'
import { tiptapToText } from '@render/lib/tiptapToText'
import BaseInputShell from '../BaseInput/BaseInputShell.vue'
import BaseInputField from '../BaseInput/BaseInputField.vue'
import TipTap from '@render/lib/TipTap'
import type { TextInputFields } from './definition'

const emit = defineEmits<{
  (e: 'onSubmit'): void
}>()

const wrapperEl = ref<HTMLElement | null>(null)

const ensureVisible = () => {
  setTimeout(() => {
    if (wrapperEl.value) {
      wrapperEl.value.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, 300)
}

const handleFocus = () => {
  ensureVisible()
}

const fields = defineModel<TextInputFields>('fields', {
  required: true,
  default: () => ({
    placeholder: undefined,
    validations: [],
    validationMessage: undefined,
    valueVariable: undefined,
    isValidVariable: undefined,
  }),
})

const pipe = usePipe()

// "off" doesn't stop Chrome's payment-suggestion heuristic (see AgeInput/
// WeightInput/HeightInput) — fall back to the same one-time-code mitigation
// unless the author picked a real autofill token from the dropdown.
const resolvedAutocomplete = computed(() => {
  const token = fields.value.autocomplete
  return !token || token === 'off' ? 'one-time-code' : token
})
const idBase = useId()
const inputId = computed(() => `${idBase}-${resolvedAutocomplete.value}`)

const outerLabelValue = useTranslation(() => fields.value.outerLabel?.key)
const validationMessageValue = useTranslation(() => fields.value.validationMessage?.key)
const placeholderText = computed(() =>
  tiptapToText(pipe.getTranslation(fields.value.placeholder?.key)),
)

const validators: Record<string, (v: string) => boolean> = {
  notEmpty: (v) => !!v && v.trim().length > 0,
}

function isValid(v: string): boolean {
  const selected = fields.value.validations ?? []
  return selected.every((id) => validators[id]?.(v) ?? true)
}

const value = computed<string>({
  get() {
    return fields.value.valueVariable
      ? ((pipe.getData(`var.${fields.value.valueVariable?.key ?? ''}`) ?? '') as string)
      : ''
  },
  set(val) {
    if (!fields.value.valueVariable) return
    // Transient: keep the reactive store live but defer the localStorage
    // persist until the form is submitted (committed via submitFormGroup).
    pipe.setData(`var.${fields.value.valueVariable?.key ?? ''}`, val, { persist: false })
    if (fields.value.isValidVariable?.key) {
      pipe.setData(`var.${fields.value.isValidVariable.key}`, isValid(val), { persist: false })
    }
  },
})

const formInput = useFormInput({
  validate: () => isValid(value.value),
  disableRealtimeValidation: () => fields.value.disableRealtimeValidation === true,
  persistKeys: () => [fields.value.valueVariable?.key, fields.value.isValidVariable?.key],
})
const { showErrors, markTouched, requestSubmit } = formInput

// Preserves the legacy "only flip touched when the field has content"
// heuristic so first-touch empty inputs don't immediately turn red.
function handleBlur() {
  if (value.value !== '') formInput.handleBlur()
}

const hasError = computed(() => showErrors.value && !isValid(value.value))

// The author's label and error copy sit OUTSIDE the <label> shell, so they are
// associated by id rather than by wrapping — see `useInputAria`.
const { labelId, errorId, describedBy } = useInputAria()
const showValidationMessage = computed(() => hasError.value && !!validationMessageValue.value)

function onInput(val: number | string) {
  value.value = String(val)
}

async function handleSubmit() {
  const { targetInvoked } = await requestSubmit()
  if (targetInvoked) return
  // Legacy fallback — no CTA in scope, preserve pre-Form behavior so
  // existing pipes wiring `onSubmit` directly on this input keep working.
  if (!isValid(value.value)) {
    markTouched()
    return
  }
  emit('onSubmit')
}

useComponentScopedVariables('TextInput', () => ({
  value: value.value,
}))

const overrideLabelColor = computed(
  () => getColorStyleData(fields.value.outerLabelColor) ?? 'var(--color-text-secondary)',
)
const overrideLabelFontSize = computed(
  () => getSizeStyleData(fields.value.outerLabelFontSize) ?? 'var(--text-body-sm-size)',
)
const overrideLabelLineHeight = computed(
  () => getSizeStyleData(fields.value.outerLabelLineHeight) ?? 'var(--text-body-sm-line-height)',
)
const overrideLabelFontWeight = computed(
  () => fields.value.outerLabelFontWeight ?? 'var(--text-body-sm-weight)',
)
const overrideLabelTextAlign = computed(() => fields.value.outerLabelTextAlign ?? 'left')
const overrideLabelMarginBottom = computed(
  () => getSizeStyleData(fields.value.outerLabelMarginBottom) ?? 'var(--space-8)',
)
const overrideBgColor = computed(() => getColorStyleData(fields.value.bgColor))
const overrideBgColorError = computed(() => getColorStyleData(fields.value.bgColorError))
const overrideTextColor = computed(() => getColorStyleData(fields.value.textColor))
const overrideTextColorDisabled = computed(() => getColorStyleData(fields.value.textColorDisabled))
const overrideTextModeFontSize = computed(() => getSizeStyleData(fields.value.textModeFontSize))
const overrideTextModeLineHeight = computed(() => getSizeStyleData(fields.value.textModeLineHeight))
const overridePlaceholderColor = computed(() => getColorStyleData(fields.value.placeholderColor))
const overrideBorderColor = computed(() => getColorStyleData(fields.value.borderColor))
const overrideBorderColorHover = computed(() => getColorStyleData(fields.value.borderColorHover))
const overrideBorderColorActive = computed(() => getColorStyleData(fields.value.borderColorActive))
const overrideBorderColorError = computed(() => getColorStyleData(fields.value.borderColorError))
const overrideBorderRadius = computed(() => getSizeStyleData(fields.value.borderRadius))
const overrideMinHeight = computed(() => getSizeStyleData(fields.value.minHeight))
const overrideMaxWidth = computed(() => getSizeStyleData(fields.value.maxWidth) ?? '100%')
const overridePadding = computed(() => getSizeStyleData(fields.value.padding))

const shellProps = computed(() => ({
  bgColor: overrideBgColor.value,
  bgColorError: overrideBgColorError.value,
  borderColor: overrideBorderColor.value,
  borderColorHover: overrideBorderColorHover.value,
  focusBorderColor: overrideBorderColorActive.value,
  borderColorError: overrideBorderColorError.value,
  borderRadius: overrideBorderRadius.value,
  minHeight: overrideMinHeight.value,
  maxWidth: overrideMaxWidth.value,
  padding: overridePadding.value,
}))

const fieldProps = computed(() => ({
  textColor: overrideTextColor.value,
  textColorDisabled: overrideTextColorDisabled.value,
  textColorPlaceholder: overridePlaceholderColor.value,
  textModeFontSize: overrideTextModeFontSize.value,
  textModeLineHeight: overrideTextModeLineHeight.value,
  textModeFontWeight: fields.value.textModeFontWeight,
}))
</script>

<style scoped lang="scss">
.text-input {
  position: relative;

  &__label {
    max-width: v-bind(overrideMaxWidth);
    margin-left: auto;
    margin-right: auto;
    margin-bottom: v-bind(overrideLabelMarginBottom);
    color: v-bind(overrideLabelColor);
    font-size: v-bind(overrideLabelFontSize);
    font-weight: v-bind(overrideLabelFontWeight);
    line-height: v-bind(overrideLabelLineHeight);
    text-align: v-bind(overrideLabelTextAlign);
  }

  &__error {
    margin-top: var(--space-4);
    font-size: var(--text-body-sm-size);
    line-height: var(--text-body-sm-line-height);
    text-align: center;
    color: var(--color-feedback-error-text);
  }
}
</style>
